import { Component, inject, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, switchMap, of, tap, takeUntil } from 'rxjs';
import { formControl, formGroup, Validators } from '@al00x/forms';
import {
  ButtonClickEvent,
  CacButtonComponent,
  CacFieldComponent,
  CacImageUploaderComponent,
  CacSwitchComponent,
  CacIconComponent
} from '@controladad/ng-base';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PaymentMethodEntity,
  PaymentMethodUpdate,
  PaymentMethodType,
  LocalizedString
} from '@models';
import { PaymentMethodApiService, UploadResult } from '@core';

@Component({
  selector: 'lib-payment-methods',
  standalone: true,
  imports: [
    CommonModule,
    CacButtonComponent,
    CacFieldComponent,
    CacImageUploaderComponent,
    CacSwitchComponent,
    CacIconComponent,
    MatProgressSpinnerModule
  ],
  templateUrl: './payment-methods.component.html',
  styleUrl: './payment-methods.component.scss',
})
export class PaymentMethodsComponent {
  private readonly paymentMethodApi = inject(PaymentMethodApiService);
  private readonly destroyRef = inject(DestroyRef);

  paymentMethods = signal<PaymentMethodEntity[]>([]);
  selectedPaymentMethod = signal<PaymentMethodEntity | undefined>(undefined);
  isLoadingList = signal(false);
  isLoadingMethod = signal(false);

  form = formGroup({
    englishName: formControl<string>(undefined, [Validators.required]),
    arabicName: formControl<string>(undefined, [Validators.required]),
    image: formControl<string | UploadResult | null | undefined>(undefined, [Validators.required]),
    status: formControl<boolean>(true),
  });

  constructor() {
    this.loadPaymentMethods();
  }

  private loadPaymentMethods() {
    this.isLoadingList.set(true);
    this.paymentMethodApi.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (methods) => {
          this.paymentMethods.set(methods);
          this.isLoadingList.set(false);
        },
        error: (error) => {
          console.error('Error loading payment methods:', error);
          snackbar$.error('Failed to load payment methods');
          this.isLoadingList.set(false);
        }
      });
  }

  selectPaymentMethod(method: PaymentMethodEntity) {
    this.selectedPaymentMethod.set(method);
    this.isLoadingMethod.set(true);

    // Populate form with selected method data
    this.form.patchValue({
      englishName: method.name.english || '',
      arabicName: method.name.arabic || '',
      image: method.image && method.image.url ? method.image.url : null,
      status: method.status === 1,
    });

    this.isLoadingMethod.set(false);
  }

  onSubmit(e: ButtonClickEvent) {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      snackbar$.error('Please fill in all required fields');
      return;
    }

    const selectedMethod = this.selectedPaymentMethod();
    if (!selectedMethod) {
      snackbar$.error('No payment method selected');
      return;
    }

    const values = this.form.getRawValue();

    const updateData: PaymentMethodUpdate = {
      paymentMethodId: selectedMethod.id,
      fields: {
        name: {
          english: values.englishName || null,
          arabic: values.arabicName || null,
        },
        image: this.getImagePath(values.image, selectedMethod.image && selectedMethod.image.path ? selectedMethod.image.path : undefined),
        state: values.status ? 1 : 0
      }
    };

    this.paymentMethodApi.update(updateData)
      .pipe(e.pipe())
      .subscribe({
        next: () => {
          snackbar$.show('Payment method updated successfully');
          this.loadPaymentMethods();
          
          // Update the selected method in the signal
          const updatedMethod = {
            ...selectedMethod,
            name: {
              english: values.englishName || null,
              arabic: values.arabicName || null,
            },
            status: values.status ? 1 : 0,
          };
          this.selectedPaymentMethod.set(updatedMethod);
        },
        error: (error) => {
          console.error('Error updating payment method:', error);
          snackbar$.error('Failed to update payment method');
        }
      });
  }

  getPaymentMethodTypeName(type: number): string {
    const typeNames: { [key: number]: string } = {
      [PaymentMethodType.CreditCard]: 'Credit Card',
      [PaymentMethodType.DebitCard]: 'Debit Card',
      [PaymentMethodType.BankTransfer]: 'Bank Transfer',
      [PaymentMethodType.DigitalWallet]: 'Digital Wallet',
      [PaymentMethodType.CashOnDelivery]: 'Cash on Delivery',
      [PaymentMethodType.Other]: 'Other',
    };
    return typeNames[type] || 'Unknown';
  }

  private getImagePath(image: string | UploadResult | null | undefined, currentPath?: string): string | null {
    if (!image) return null;
    
    if (typeof image === 'string') {
      return currentPath || null;
    }
    
    return image.imagePath || null;
  }
}
